import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MOCK_SOLVE_RESPONSE } from '../../data/mock/problem';
import { StepOutline } from './StepOutline';
import { StepViewer } from './StepViewer';
import { CoachPanel } from './CoachPanel';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../../components/ui/resizable';
import { copyToClipboard } from '../../lib/utils';
import { useMobile } from '../../hooks/use-mobile';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { Menu, Loader2, Sparkles, Send, Share2, Check, ImagePlus, X, Volume2, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { solveProblem, createShare, getSession } from '../../api/tutor';
import { uploadFile, getFileUrl } from '../../api/qb';
import { SolveResponse } from '../../types/tutor';
import { toast } from 'sonner';

export function TutorLayout() {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<SolveResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [inputContent, setInputContent] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string>('');
  const autoTriggeredRef = useRef(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  
  // Share state
  const [isSharing, setIsSharing] = useState(false);
  const hasImage = Boolean(imageUrl);
  const hasUsableImage = hasImage && !imageUrl?.startsWith('blob:');

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [imagePreviewUrl]);

  const handlePlayAudio = (audioUrl: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => {
      setIsPlaying(false);
      toast.error('音频播放失败');
    };
    audio.play().catch(() => setIsPlaying(false));
  };

  // Load shared session if present
  useEffect(() => {
    const shareId = searchParams.get('share_id');
    if (shareId && !data && !loading) {
      const loadSession = async () => {
        setLoading(true);
        try {
          const session = await getSession(shareId);
          setData(session.result_json);
          setInputContent(session.input_content);
          if (session.result_json.steps.length > 0) {
            setCurrentStepId(session.result_json.steps[0].step_id);
          }
          toast.success('已加载分享的解题内容');
        } catch (error) {
          console.error(error);
          toast.error('加载分享内容失败，内容可能已过期');
        } finally {
          setLoading(false);
        }
      };
      loadSession();
    }
  }, [searchParams]); // Only depend on searchParams change

  const activeData = data; 
  const canSubmit = Boolean(inputContent.trim()) || hasUsableImage;

  const executeSolve = async (content: string, questionId?: string | null) => {
    const normalizedContent = content.trim();
    if (!normalizedContent && !hasUsableImage) {
      toast.error('请至少输入题目文本或上传图片');
      return;
    }
    if (hasImage && !hasUsableImage) {
      toast.error('图片尚未上传完成，请重新上传后再试');
      return;
    }
    
    // Clear share_id if starting new solve
    if (searchParams.get('share_id')) {
      setSearchParams({});
    }

    setLoading(true);
    try {
      const response = await solveProblem({ content: normalizedContent, question_id: questionId || undefined, image_url: hasUsableImage ? imageUrl || undefined : undefined });
      setData(response);
      if (response.steps.length > 0) {
        setCurrentStepId(response.steps[0].step_id);
      }
      toast.success('解题成功！');
    } catch (error) {
      console.error(error);
      toast.error('解题失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('仅支持上传图片文件');
      return;
    }
    let previewUrl: string | null = null;
    setUploadingImage(true);
    try {
      const preview = URL.createObjectURL(file);
      previewUrl = preview;
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(preview);
      const uploaded = await uploadFile(file);
      const resolvedImageUrl = uploaded.url ? getFileUrl(uploaded.url) : getFileUrl(uploaded.file_id);
      if (resolvedImageUrl.startsWith('blob:')) {
        throw new Error('图片地址仍是本地预览地址');
      }
      setImageUrl(resolvedImageUrl);
      setImageName(file.name);
      toast.success('图片上传成功，已保存为服务器地址');
    } catch (error) {
      console.error(error);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setImagePreviewUrl(null);
      setImageUrl(null);
      setImageName('');
      toast.error('图片上传失败，请重试');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    setImageUrl(null);
    setImageName('');
  };

  const handleSolve = () => executeSolve(inputContent);

  useEffect(() => {
    let mode = searchParams.get('mode');
    let autoTrigger = searchParams.get('auto_trigger');
    if (!mode || !autoTrigger) {
      const hash = window.location.hash || '';
      const queryIndex = hash.indexOf('?');
      if (queryIndex >= 0) {
        const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
        mode = mode || hashParams.get('mode');
        autoTrigger = autoTrigger || hashParams.get('auto_trigger');
      }
    }

    const pendingExplain = localStorage.getItem('tutor_context_pending') === 'true';
    const shouldAutoTrigger =
      !autoTriggeredRef.current &&
      !data &&
      !loading &&
      ((mode === 'explain' && autoTrigger === 'true') || pendingExplain);

    if (shouldAutoTrigger) {
      const storedContent = localStorage.getItem('tutor_context_content');
      const storedQuestionId = localStorage.getItem('tutor_context_question_id');
      if (storedContent) {
        autoTriggeredRef.current = true;
        setInputContent(storedContent);
        executeSolve(storedContent, storedQuestionId);
        localStorage.removeItem('tutor_context_pending');
        localStorage.removeItem('tutor_context_content');
        localStorage.removeItem('tutor_context_question_id');
        
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('auto_trigger');
        setSearchParams(newParams);
      }
    }
  }, [searchParams, data, loading, setSearchParams]);

  const handleShare = async () => {
    if (!activeData || !inputContent) return;
    
    setIsSharing(true);
    try {
      const res = await createShare({
        input_content: inputContent,
        result_json: activeData
      });
      
      // Construct share URL
      // Ensure we use the current origin and path, adding hash if needed
      const baseUrl = window.location.href.split('#')[0];
      const shareUrl = `${baseUrl}#/tutor?share_id=${res.share_id}`;
      
      const success = await copyToClipboard(shareUrl);
      if (success) {
        toast.success('分享链接已复制到剪贴板！');
      } else {
        toast.error('无法自动复制链接，请手动复制', {
          duration: 10000,
          description: shareUrl,
          action: {
            label: '复制',
            onClick: () => {
                window.prompt("请复制以下链接:", shareUrl);
            }
          }
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('创建分享失败: ' + (error instanceof Error ? error.message : '未知错误'));
    } finally {
      setIsSharing(false);
    }
  };

  const handleOpenVisualization = () => {
    const content = inputContent.trim();
    const search = new URLSearchParams();
    if (content) {
      search.set('content', content);
    }
    if (hasUsableImage && imageUrl) {
      search.set('image_url', imageUrl);
    }
    const suffix = search.toString();
    navigate(`/tutor/visualization${suffix ? `?${suffix}` : ''}`);
  };

  // If no data, show Input State
  if (!activeData) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-blue-200">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">AI 数学导师</h1>
            <p className="text-slate-500">输入题目，我来为你详细讲解</p>
          </div>
          
          <div className="bg-white p-4 rounded-xl shadow-sm border space-y-4">
            <Textarea 
              placeholder="请输入数学题目..." 
              className="min-h-[120px] text-base resize-none"
              value={inputContent}
              onChange={(e) => setInputContent(e.target.value)}
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage || loading}
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    上传中...
                  </>
                ) : (
                  <>
                    <ImagePlus className="mr-2 h-4 w-4" />
                    上传题目图片
                  </>
                )}
              </Button>
              {imagePreviewUrl && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <img src={imagePreviewUrl} alt="题目图片预览" className="max-h-48 w-full rounded object-contain bg-slate-50" />
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate">{imageName}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={handleRemoveImage}>
                      <X className="mr-1 h-3 w-3" />
                      移除
                    </Button>
                  </div>
                  <div className="rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-700 break-all">
                    已保存地址：{imageUrl || '上传处理中...'}
                  </div>
                  {imageUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={async () => {
                        const copied = await copyToClipboard(imageUrl);
                        if (copied) {
                          toast.success('图片地址已复制');
                        } else {
                          toast.error('复制失败');
                        }
                      }}
                    >
                      复制图片地址
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Button 
              className="w-full h-12 text-lg font-medium" 
              onClick={handleSolve}
              disabled={loading || uploadingImage || !canSubmit}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  正在思考...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  开始讲解
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleOpenVisualization}
              disabled={loading || uploadingImage}
            >
              打开图示页面
            </Button>
          </div>
          
          <div className="text-center">
            <Button variant="link" className="text-slate-400" onClick={() => {
              setData(MOCK_SOLVE_RESPONSE);
              setCurrentStepId(MOCK_SOLVE_RESPONSE.steps[0].step_id);
            }}>
              试用演示数据
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Find current step object
  const currentStep = activeData.steps.find(s => s.step_id === currentStepId) || activeData.steps[0];
  const currentStepIndex = activeData.steps.findIndex(s => s.step_id === currentStepId);

  // Handlers
  const handleStepClick = (id: string) => setCurrentStepId(id);
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepId(activeData.steps[currentStepIndex - 1].step_id);
    }
  };
  const handleNextStep = () => {
    if (currentStepIndex < activeData.steps.length - 1) {
      setCurrentStepId(activeData.steps[currentStepIndex + 1].step_id);
    }
  };

  // Mobile Layout (Focus Stream)
  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-white">
        {/* Mobile Header */}
        <header className="h-14 border-b flex items-center px-4 bg-white sticky top-0 z-10 justify-between">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="-ml-2">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80%] p-0">
                <StepOutline 
                  steps={activeData.steps} 
                  currentStepId={currentStepId} 
                  onStepClick={handleStepClick}
                  className="h-full border-none"
                />
              </SheetContent>
            </Sheet>
            <span className="font-semibold text-slate-800 truncate max-w-[200px]">
              {currentStep.title}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleOpenVisualization}>
              图示页
            </Button>
            {/* Share Button Mobile */}
            <Button variant="ghost" size="icon" onClick={handleShare} disabled={isSharing}>
              {isSharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            </Button>
            <div className="text-xs font-mono text-slate-400">
              {currentStepIndex + 1}/{activeData.steps.length}
            </div>
          </div>
        </header>

        {/* Mobile Content (Scrollable) */}
        <div className="flex-1 overflow-y-auto pb-20">
          <StepViewer 
            problem={activeData.problem}
            currentStep={currentStep}
            historySteps={activeData.steps.slice(0, currentStepIndex)}
            mode={activeData.mode}
            geometryLesson={activeData.geometry_lesson}
          />
        </div>

        {/* Mobile Coach (Fixed Bottom) */}
        <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-2">
           <CoachPanel 
             currentStep={currentStep} 
             nextStepText={activeData.steps[currentStepIndex + 1]?.speak_text}
             nextStepAudioUrl={activeData.steps[currentStepIndex + 1]?.audio_url}
             onNextStep={handleNextStep}
           />
        </div>
      </div>
    );
  }

  // Desktop Layout (Three Panes)
  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      {/* Desktop Header */}
      <header className="h-14 bg-white border-b flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-bold text-slate-800">AI 数学导师</h1>
        </div>
        <div className="flex items-center gap-2">
             <Button variant="ghost" size="sm" onClick={handleOpenVisualization}>
                图示页
            </Button>
             <Button variant="outline" size="sm" onClick={handleShare} disabled={isSharing}>
                {isSharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
                分享解题
            </Button>
             <Button variant="ghost" size="sm" onClick={() => setData(null)}>
                新题目
            </Button>
        </div>
      </header>

      {/* Desktop Layout — Center only */}
      <div className="flex-1 overflow-hidden bg-slate-50/50">
        <div className="h-full overflow-y-auto p-8">
          <div className="max-w-3xl mx-auto pb-20">
            <StepViewer
              problem={activeData.problem}
              currentStep={currentStep}
              historySteps={activeData.steps.slice(0, currentStepIndex)}
              mode={activeData.mode}
              geometryLesson={activeData.geometry_lesson}
            />

            {/* Navigation Footer */}
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="outline"
                size="lg"
                onClick={handlePrevStep}
                disabled={currentStepIndex <= 0}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                上一步
              </Button>

              <div className="flex items-center gap-3">
                {currentStep.audio_url && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => handlePlayAudio(currentStep.audio_url!)}
                    disabled={isPlaying}
                  >
                    <Volume2 className="mr-2 h-4 w-4" />
                    {isPlaying ? '播放中...' : 'AI老师讲解'}
                  </Button>
                )}
                <Button
                  size="lg"
                  className="shadow-lg shadow-blue-100"
                  onClick={handleNextStep}
                  disabled={currentStepIndex >= activeData.steps.length - 1}
                >
                  {currentStepIndex >= activeData.steps.length - 1 ? '完成讲解' : '下一步'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
